/**
 * Map CMS QPP eligibility "CLINICIAN TYPE" / QPP API typeDescription strings.
 * Buckets (physician / app / other) are for rollups; display labels keep the
 * specific CMS type so rosters show NP, PA, social worker, etc.
 * Expand as we see new values in real files — never invent type from specialty alone.
 */

export type ClinicianTypeBucket = 'physician' | 'app' | 'other';

const PHYSICIAN_TYPES = new Set([
  'doctor of medicine',
  'doctor of osteopathy',
  'doctor of dental medicine',
  'doctor of dental surgery',
  'doctor of podiatric medicine',
  'doctor of optometry',
  'doctor of chiropractic',
]);

/** Advanced practice providers commonly grouped as APP in MIPS contexts. */
const APP_TYPES = new Set([
  'nurse practitioner',
  'physician assistant',
  'certified nurse midwife',
  'clinical nurse specialist',
  'certified registered nurse anesthetist',
]);

/** Short, readable labels for roster tables (key = lowercased CMS string). */
const DISPLAY_LABELS: Record<string, string> = {
  'doctor of medicine': 'MD',
  'doctor of osteopathy': 'DO',
  'doctor of dental medicine': 'DMD',
  'doctor of dental surgery': 'DDS',
  'doctor of podiatric medicine': 'DPM',
  'doctor of optometry': 'OD',
  'doctor of chiropractic': 'DC',
  'nurse practitioner': 'NP',
  'physician assistant': 'PA',
  'certified nurse midwife': 'CNM',
  'clinical nurse specialist': 'CNS',
  'certified registered nurse anesthetist': 'CRNA',
  'clinical social worker': 'Clinical social worker',
  'licensed clinical social worker': 'Clinical social worker',
  'clinical psychologist': 'Clinical psychologist',
  'psychologist': 'Psychologist',
  'registered dietitian or nutrition professional': 'RD / nutrition',
  'registered dietitian': 'RD',
  'audiologist': 'Audiologist',
  'physical therapist': 'Physical therapist',
  'occupational therapist': 'Occupational therapist',
  'speech language pathologist': 'SLP',
  'speech-language pathologist': 'SLP',
  'certified nurse practitioner': 'NP',
};

function normalizeTypeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function bucketClinicianType(raw: string | null | undefined): ClinicianTypeBucket | null {
  if (!raw || !String(raw).trim()) return null;
  const key = normalizeTypeKey(raw);
  if (
    PHYSICIAN_TYPES.has(key)
    || key.includes('doctor of medicine')
    || key.includes('doctor of osteopathy')
  ) {
    return 'physician';
  }
  if (
    APP_TYPES.has(key)
    || key.includes('nurse practitioner')
    || key.includes('physician assistant')
  ) {
    return 'app';
  }
  return 'other';
}

export function clinicianTypeBucketLabel(bucket: ClinicianTypeBucket): string {
  if (bucket === 'physician') return 'Physician';
  if (bucket === 'app') return 'APP';
  return 'Other';
}

/**
 * Specific CMS clinician type for UI (NP, PA, Clinical social worker, …).
 * Prefer this over the coarse bucket anywhere users pick or scan a roster.
 */
export function formatClinicianTypeLabel(raw: string | null | undefined): string | null {
  if (!raw || !String(raw).trim()) return null;
  const key = normalizeTypeKey(raw);
  if (DISPLAY_LABELS[key]) return DISPLAY_LABELS[key];
  // Partial matches for noisy CMS variants
  if (key.includes('nurse practitioner')) return 'NP';
  if (key.includes('physician assistant')) return 'PA';
  if (key.includes('social worker')) return 'Clinical social worker';
  if (key.includes('clinical psychologist') || key === 'psychologist') return 'Clinical psychologist';
  if (key.includes('anesthetist') || key.includes('crna')) return 'CRNA';
  if (key.includes('dietitian') || key.includes('nutrition')) return 'RD / nutrition';
  // Fallback: keep CMS wording, lightly cleaned
  return raw.trim().replace(/\s+/g, ' ');
}
